// Portfolio Administration v1.0 PR 4a §7.5 — File UCC-3 Continuation modal.
//
// Continuation must be filed within the 6 months prior to lapse (UCC §9-515(d)).
// The backend enforces; this UI gives early feedback by warning when the
// chosen filingDate is outside the continuation window.

import { useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useFileUccContinuation } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import type { UccFilingDto } from "@/api/portfolioAdmin/types";
import { formatCalendarDate, isoDateInTz, useUserTimeZone } from "@/utils/datetime";

interface FileContinuationModalProps {
  loanId: string;
  parent: UccFilingDto;
  open: boolean;
  onClose: () => void;
}

/** Continuation window = 180 days prior to lapse. */
function withinContinuationWindow(filingDateIso: string, lapseDateIso: string): boolean {
  const filing = new Date(filingDateIso).getTime();
  const lapse = new Date(lapseDateIso).getTime();
  if (Number.isNaN(filing) || Number.isNaN(lapse)) return false;
  const windowStart = lapse - 180 * 86_400_000;
  return filing >= windowStart && filing <= lapse;
}

export function FileContinuationModal({
  loanId: _loanId,
  parent,
  open,
  onClose,
}: FileContinuationModalProps) {
  const toast = useToast();
  const fileContinuation = useFileUccContinuation(_loanId);

  const tz = useUserTimeZone();
  const today = isoDateInTz(new Date(), tz);
  const [filingDate, setFilingDate] = useState(today);
  const [filingNumber, setFilingNumber] = useState("");

  const windowOk = withinContinuationWindow(filingDate, parent.lapseDate);

  const onSubmit = async () => {
    if (!filingNumber.trim()) {
      toast.error("Filing number is required.");
      return;
    }
    try {
      await fileContinuation.mutateAsync({
        parentFilingId: parent.id,
        filingDate,
        filingNumber,
      });
      toast.success("Continuation filed.");
      setFilingNumber("");
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiError && err.errors.length
          ? err.errors.join("; ")
          : err instanceof Error
            ? err.message
            : "Could not file continuation.",
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title="File UCC-3 Continuation"
      description={`Parent: ${parent.filingNumber} · Lapse ${formatCalendarDate(parent.lapseDate)}`}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            onClick={() => void onSubmit()}
            disabled={fileContinuation.isPending || !filingNumber.trim()}
            data-testid="ucc-continuation-submit"
          >
            File continuation
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3" data-testid="ucc-continuation-form">
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Filing date</span>
          <input
            type="date"
            className="h-9 px-space-2 border border-border-light rounded-md text-small"
            value={filingDate}
            onChange={(e) => setFilingDate(e.target.value)}
            data-testid="ucc-continuation-filingDate"
          />
        </label>
        <label className="flex flex-col gap-space-1 text-caption">
          <span className="text-gray-mid uppercase tracking-label">Filing number</span>
          <input
            className="h-9 px-space-2 border border-border-light rounded-md text-small font-mono"
            value={filingNumber}
            onChange={(e) => setFilingNumber(e.target.value)}
            data-testid="ucc-continuation-filingNumber"
          />
        </label>
        {!windowOk && (
          <p
            className="text-caption text-status-warning m-0"
            data-testid="ucc-continuation-window-warning"
          >
            Filing date is outside the 6-month continuation window. The server
            will reject the filing.
          </p>
        )}
      </div>
    </Modal>
  );
}
